<script>
import CertPills from '@components/cert-pills.vue'
import GradeList from '@/components/booking/grade-list.vue'
import BookingListPolicies from '@components/booking/booking-list-policies.vue'
import formatDateMixin from '@/mixins/formatDateMixin'
import baseMixin from '@/mixins/baseMixin'
import bookingListMixin from '@/mixins/bookingListMixin'
import ClientBookingStatus from '@/shared/constants/booking/ClientBookingStatus'
import BookingListFeedback from '@components/booking/booking-list-feedback.vue'
import BookingCardMenu from './booking-card-menu.vue'

export default {
  components: {
    CertPills,
    BookingListPolicies,
    GradeList,
    BookingListFeedback,
    BookingCardMenu,
  },
  mixins: [baseMixin, formatDateMixin, bookingListMixin],
  props: {
    bookings: {
      type: Array,
      default: function() {
        return []
      },
    },
  },
  methods: {
    bookingHasCandidate(booking) {
      return booking.candidate && booking.candidate.id > 0
    },
    generateBookingNameString(booking) {
      if (this.bookingHasCandidate(booking))
        return `${booking.candidate.firstName} ${booking.candidate.lastName}`
      return this.$t('booking.unfilledBookingText')
    },
    generateBookingTimeRangeString(booking) {
      return `${this.renderTime24(
        booking.startDateTimeLocal
      )} - ${this.renderTime24(booking.endDateTimeLocal)}`
    },
    generateReplacingString(booking) {
      const name = `${booking.replacing?.firstName ?? ''} ${booking.replacing
        ?.lastName ?? ''}`
      return name.trim()
    },
    showCancelBookingDialog(booking) {
      this.$emit('cancelBooking', booking)
    },
    determineIfBookingHasGroup(booking) {
      return !!booking?.blockText
    },
    bookingIsPendingApproval(booking) {
      return booking.status === ClientBookingStatus.pendingApproval
    },
  },
}
</script>

<template>
  <section>
    <v-data-table
      :headers="columns"
      :items="bookings"
      class="custom-elevation rounded-xl mb-8"
      hide-default-footer
      disable-sort
    >
      <template v-slot:[`item.id`]="{ item }">
        <div class="d-flex align-center">
          {{ `#${item.id}` }}
          <span v-if="determineIfBookingHasGroup(item)" class="cursor-help">
            <v-tooltip :right="!isMobileViewPort" :top="isMobileViewPort">
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  {{ ` - ${item.blockText}` }}
                </span>
              </template>
              <span>
                {{ item.blockTooltipText }}
              </span>
            </v-tooltip>
          </span>
        </div>

        <v-chip
          v-if="bookingIsPendingApproval(item)"
          text
          color="warning"
          x-small
          >{{ $t('booking.pendingApproval.label') }}</v-chip
        >
      </template>

      <template v-slot:[`item.name`]="{ item }">
        {{ generateBookingNameString(item) }}
      </template>

      <template v-slot:[`item.time`]="{ item }">
        {{ generateBookingTimeRangeString(item) }}
      </template>

      <template v-slot:[`item.grades`]="{ item }">
        <GradeList :grades="item.grades" />
      </template>

      <template v-slot:[`item.certifications`]="{ item }">
        <CertPills :certifications="item.certChecks" />
      </template>

      <template v-slot:[`item.policies`]="{ item }">
        <BookingListPolicies :policies="item.agreements" />
      </template>

      <template v-slot:[`item.replacing`]="{ item }">
        <span class="booking-list__replacing">
          {{ generateReplacingString(item) }}
        </span>
      </template>

      <template v-slot:[`item.feedback`]="{ item }">
        <BookingListFeedback
          v-if="bookingHasCandidate(item) && !bookingIsPendingApproval(item)"
          :booking-id="item.id"
          :candidate="item.candidate"
          :feedback="item.feedback"
        />
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <BookingCardMenu
          v-if="!bookingIsPendingApproval(item)"
          @showCancelBookingDialog="showCancelBookingDialog(item)"
        />
      </template>
    </v-data-table>
  </section>
</template>
